/**
 * useSearchData — Phase 0 lean shell hook (was: SharedDataStore /search/all)
 *
 * This hook used to fetch /search/all, which returned ~135MB of fully
 * denormalized orgs + deals + products + parent rollups and took 5–30s
 * depending on network. Every page that imports this hook (or
 * useEntitiesFromSearch which wraps it) shared that cost.
 *
 * Phase 0 rewires the hook to call /search/minimal instead, which
 * returns ~1MB of lookup-only org + deal data in ~200ms–1.5s. The
 * return shape is preserved for backwards compatibility:
 *
 *   - organizations / deals: populated from /search/minimal (now lean
 *     records — see MinimalOrg / MinimalDeal in useMinimalShell.ts).
 *   - products / parentClients: ALWAYS RETURNED AS EMPTY ARRAYS now.
 *     Components that legitimately need products must fetch per-route
 *     (per-deal: queryAllItemsByPK, products grid: useServerSideProducts).
 *     Components that legitimately need the aggregated parentClients
 *     view must fetch that separately too — but most consumers really
 *     just needed the org-level contractedBy/affiliated fields, which
 *     ARE in the minimal org records.
 *   - custom_products: empty derived from empty products — same caveat.
 *
 * Migration semantics: this is a soft cut. Existing callers that read
 * organizations + deals continue to work. Callers that destructure
 * products / parentClients get an empty array — they'll render
 * empty-state instead of stale data until each one is migrated to a
 * per-route fetch. Most of the audit-identified products consumers
 * are deal-scoped or org-scoped anyway and have local fetches
 * available; the dashboard-wide consumers (AccountingPage, the WS
 * handler in useAllItemsLive) need explicit follow-up work.
 *
 * Usage:
 * ```tsx
 * const { organizations, deals, isLoading } = useSearchData()
 * ```
 *
 * Invalidate with:
 * ```tsx
 * queryClient.invalidateQueries({ queryKey: SEARCH_DATA_KEY })
 * ```
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useEffect, useMemo } from 'react'
import type { MinimalShellResponse } from './useMinimalShell'

const API_BASE = baseUrl || 'https://grobotic.app:2096'

/** Query key for search data — kept stable so existing
 *  invalidateQueries calls across the codebase continue to land. */
export const SEARCH_DATA_KEY = ['search', 'allData'] as const

/**
 * The heavy /search/all fetch, deliberately outside SEARCH_DATA_KEY.
 *
 * Under `['search', 'allData', 'full']` it matched every
 * `invalidateQueries({ queryKey: SEARCH_DATA_KEY })` by prefix, so each
 * approval or save across the app re-downloaded ~20MB per open tab and made
 * the server rebuild it (23 Oct 2026). Those calls refresh the lean payload
 * now; this one refreshes on its own staleness, or through `refresh()`.
 */
export const FULL_SEARCH_DATA_KEY = ['search', 'fullData'] as const

/** The server rebuilds its indexes every 5 minutes, so a fresher copy than
 *  that is rarely different. A 304 makes an early refetch cheap anyway. */
const FULL_STALE_MS = 5 * 60_000

export interface SearchDataMeta {
  ready: boolean
  orgsReady?: boolean
  dealsReady?: boolean
  productsReady?: boolean
  counts?: {
    organizations: number
    deals: number
    products: number
    parentClients: number
  }
  cacheAgeMs?: number | null
  lastFetchDurationMs?: number | null
  message?: string
}

export interface SearchDataResponse {
  organizations: any[]
  deals: any[]
  products: any[]
  parentClients: any[]
  meta: SearchDataMeta
}

/**
 * Merge an "incoming" (richer) list with an "existing" (leaner) list.
 *
 * For each key collision, returns a record that has the existing record's
 * fields shallow-spread over the incoming one — `{ ...incoming, ...existing }`.
 * Effect: incoming acts as the BASE shape (so heavy fields like recipients,
 * endDate, products that only exist on the full payload are populated),
 * but existing's fields take precedence per-field for anything it already
 * had (so optimistic edits applied to lean records aren't clobbered).
 *
 * The small race window: if an optimistic save updates field X to a new
 * value, and `/search/all` resolves a moment later with the pre-save
 * server X, the merge keeps the optimistic X. If the timing is reversed
 * (save commits, then we merge), we use the new server X. Either way the
 * user's value lands eventually.
 */
function mergeFullWithOverrides<T extends Record<string, unknown>>(
  existing: T[],
  incoming: T[],
  keyOf: (item: T) => string,
): T[] {
  const existingByKey = new Map<string, T>()
  for (const e of existing) existingByKey.set(keyOf(e), e)

  const merged: T[] = []
  const incomingKeys = new Set<string>()

  for (const inc of incoming) {
    const k = keyOf(inc)
    incomingKeys.add(k)
    const ex = existingByKey.get(k)
    merged.push(ex ? ({ ...inc, ...ex } as T) : inc)
  }

  // Append existing-only records (e.g. created locally since cache populated)
  for (const ex of existing) {
    if (!incomingKeys.has(keyOf(ex))) merged.push(ex)
  }

  return merged
}

/** Cache key for a product record. Products are org-scoped items in the
 *  single-table design, so PK + SK is the only unique pair. */
const productKey = (p: any): string => `${p?.PK}|${p?.SK}`

/** Fetch the lean minimal payload — orgs + deals only, ~1.5s. */
const fetchMinimalForSearchData = async (): Promise<MinimalShellResponse | null> => {
  const response = await axios.get<MinimalShellResponse>(`${API_BASE}/search/minimal`, {
    validateStatus: s => (s >= 200 && s < 300) || s === 503,
  })
  return response.data ?? null
}

/** Fetch the full /search/all payload — orgs + deals + products + parentClients,
 *  ~10-30s. This is the legacy heavy fetch; we still need it for detail pages
 *  that read products and parentClients aggregates, until each is migrated to
 *  a per-route fetch. */
const fetchFullForSearchData = async (): Promise<SearchDataResponse | null> => {
  const response = await axios.get<SearchDataResponse>(`${API_BASE}/search/all`, {
    validateStatus: s => (s >= 200 && s < 300) || s === 503,
  })
  return response.data ?? null
}

export interface UseSearchDataOptions {
  enabled?: boolean
  /** Poll interval when indexes aren't ready (default: 3000ms) */
  retryInterval?: number
  /** Stale time in ms (default: 30000ms) */
  staleTime?: number
}

export interface UseSearchDataReturn {
  // Data
  organizations: any[]
  deals: any[]
  products: any[]
  parentClients: any[]

  // Computed data (for bootstrap compatibility)
  custom_products: any[]

  // Loading state
  isLoading: boolean
  isFetching: boolean
  isReady: boolean
  error: string | null

  // Meta information
  meta: SearchDataMeta | null

  // Actions
  refresh: () => Promise<void>

  // Raw query data for advanced usage
  data: SearchDataResponse | undefined
}

/**
 * Phase 0 layered fetch:
 * 1. /search/minimal resolves first (~1.5s) → orgs + deals available immediately
 * 2. /search/all fires in parallel and resolves later (~10-30s) → products +
 *    parentClients fill in when the slower fetch completes
 *
 * Returned `organizations`/`deals` prefer the full fetch once available
 * (it carries totals, payment schedules, and other heavy fields the
 * detail pages need). They fall back to the minimal lean records until
 * then so autocompletes + shell nav work from first paint.
 *
 * Long-term path: detail pages move to per-route fetches one at a
 * time; once nothing reads products/parentClients from this hook,
 * the /search/all fetch can be removed entirely and the wait fully
 * disappears.
 */
export const useSearchData = (options: UseSearchDataOptions = {}): UseSearchDataReturn => {
  const { enabled = true, retryInterval = 3000, staleTime = 30_000 } = options

  const queryClient = useQueryClient()

  // CRITICAL: SEARCH_DATA_KEY is the cache key every optimistic save and
  // sync function in the codebase targets via setQueryData. To keep those
  // working we need this cache populated as soon as possible — so the
  // FAST minimal fetch uses this key. The full fetch lives under a
  // separate key and gets merged in via the effect below.
  //
  // queryFn returns a SearchDataResponse adapted from minimal so the cache
  // shape matches what every other consumer expects (organizations[],
  // deals[], products[], parentClients[]). products and parentClients
  // are empty until the full fetch merges in.
  const minimalQuery = useQuery({
    queryKey: SEARCH_DATA_KEY,
    queryFn: async (): Promise<SearchDataResponse> => {
      // /search/minimal carries no products or parentClients, so returning
      // empty arrays here doesn't mean "there are none" — it means "this
      // endpoint doesn't know". Writing [] anyway wiped whatever the full
      // fetch had merged in and every optimistic product patch along with it,
      // every time this query refetched. Carry the existing values forward
      // and let the full fetch (and the patches) stay authoritative for them.
      const prev = queryClient.getQueryData<SearchDataResponse>(SEARCH_DATA_KEY)
      const m = await fetchMinimalForSearchData()
      if (!m) {
        return {
          organizations: [],
          deals: [],
          products: prev?.products ?? [],
          parentClients: prev?.parentClients ?? [],
          meta: { ready: false, message: 'Empty minimal response' },
        }
      }
      return {
        organizations: m.organizations ?? [],
        deals: m.deals ?? [],
        products: prev?.products ?? [],
        parentClients: prev?.parentClients ?? [],
        meta: {
          ready: true,
          counts: {
            organizations: m.meta?.counts?.organizations ?? 0,
            deals: m.meta?.counts?.deals ?? 0,
            products: prev?.products?.length ?? 0,
            parentClients: prev?.parentClients?.length ?? 0,
          },
          lastFetchDurationMs: m.meta?.build_ms ?? null,
          message: 'minimal payload — products/parentClients filling in',
        },
      }
    },
    enabled,
    staleTime,
    gcTime: 5 * 60_000,
    // refetch-on-focus and refetch-on-reconnect were smoking guns in the
    // "data reverts after update" diagnosis. WS pushes deltas, we don't
    // need the safety-net refetch.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, retryInterval),
  })

  // Background fetch for the heavy /search/all payload. Lives under its
  // own key so it doesn't fight with the minimal one for SEARCH_DATA_KEY.
  // When it resolves, the effect below merges it INTO SEARCH_DATA_KEY,
  // enriching the cache with products + parentClients without clobbering
  // any optimistic updates that happened in between.
  const fullQuery = useQuery({
    queryKey: FULL_SEARCH_DATA_KEY,
    queryFn: fetchFullForSearchData,
    enabled,
    staleTime: Math.max(staleTime, FULL_STALE_MS),
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, retryInterval),
  })

  // Merge full data into SEARCH_DATA_KEY on arrival. Per-record:
  //   { ...full, ...existing }
  // — full provides the heavy fields the minimal payload omits
  // (recipients, endDate, products embedded on the deal, etc.) and the
  // lean fields in `existing` shallow-override them so any optimistic
  // edit the user just made survives. products + parentClients only
  // come from the full payload.
  useEffect(() => {
    const fullData = fullQuery.data
    if (!fullData) return
    queryClient.setQueryData<SearchDataResponse>(SEARCH_DATA_KEY, existing => {
      if (!existing) {
        // Minimal hasn't resolved yet — seed from full wholesale.
        return fullData
      }
      return {
        ...existing,
        organizations: mergeFullWithOverrides(existing.organizations, fullData.organizations, o => o?.PK),
        deals: mergeFullWithOverrides(existing.deals, fullData.deals, d => `${d?.PK}|${d?.SK}`),
        // Products merge the same way orgs and deals do. A straight
        // `= fullData.products` here wiped every optimistic product patch
        // (org custom services, deal product syncs) the moment /search/all
        // resolved, because SEARCH_DATA_KEY is the cache those patches write
        // to. /search/all is index-backed and up to 5 minutes stale, so the
        // patched row would vanish and only come back on the next index sync.
        products: mergeFullWithOverrides(existing.products ?? [], fullData.products ?? [], productKey),
        parentClients: fullData.parentClients ?? [],
        meta: { ...existing.meta, ...fullData.meta, ready: true },
      }
    })
  }, [fullQuery.data, queryClient])

  const data = minimalQuery.data
  const fullData = fullQuery.data
  const minimalData = minimalQuery.data

  // Combine minimal + full at READ TIME rather than relying solely on the
  // merge-into-cache effect above. That effect writes SEARCH_DATA_KEY, but
  // SEARCH_DATA_KEY is also owned by minimalQuery — so when minimal's own
  // fetch resolves AFTER the full payload has been merged in (e.g. /search/all
  // won the race, or minimal 503'd then retried), React Query overwrites the
  // key with the minimal result (products: [], and orgs/deals empty on a 503),
  // clobbering the merged full data. The dashboard then "renders for a split
  // second, then goes empty." Deriving the returned arrays from BOTH queries
  // here makes the hook's consumers immune to that cache race: once full
  // resolves its data is always reflected, regardless of what's in the shared
  // cache entry. Optimistic edits still win because they live in minimalData
  // (SEARCH_DATA_KEY) and take per-field precedence in the merge.
  const organizations = useMemo(
    () =>
      fullData?.organizations?.length
        ? mergeFullWithOverrides(minimalData?.organizations ?? [], fullData.organizations, o => o?.PK)
        : (minimalData?.organizations ?? []),
    [minimalData, fullData],
  )
  const deals = useMemo(
    () =>
      fullData?.deals?.length
        ? mergeFullWithOverrides(minimalData?.deals ?? [], fullData.deals, d => `${d?.PK}|${d?.SK}`)
        : (minimalData?.deals ?? []),
    [minimalData, fullData],
  )
  // Products are derived the same way orgs and deals are, and for the same
  // reason. Reading them straight off `fullData` made every optimistic write
  // to SEARCH_DATA_KEY a no-op — patchBootstrapCustomProductsCache and
  // syncProductCache both target that key, so an org's custom services or a
  // deal's synced products would render from the 5-minute-stale /search/all
  // payload and appear to "not save". Merging keeps the patched fields (and
  // any row full hasn't caught up on yet) until the next /search/minimal
  // refetch clears SEARCH_DATA_KEY.products, at which point server truth
  // takes over again.
  const products = useMemo(
    () => mergeFullWithOverrides(minimalData?.products ?? [], fullData?.products ?? [], productKey),
    [minimalData, fullData],
  )
  const parentClients = fullData?.parentClients ?? minimalData?.parentClients ?? []

  // Computed: custom products (for bootstrap compatibility)
  const custom_products = useMemo(() => {
    if (!products || !products.length) return []
    return products.filter(p => p?.SK?.startsWith('PRODUCT_CUSTOM_')).filter(Boolean)
  }, [products])

  // Surface progress accurately: loading is true until the minimal
  // payload (the one that unblocks first paint) has resolved. Fetching
  // stays true while the background full fetch is in flight.
  const isLoading = minimalQuery.isLoading
  const isFetching = minimalQuery.isFetching || fullQuery.isFetching
  const queryError = minimalQuery.error ?? fullQuery.error

  const isReady = !!minimalData
  const meta = data?.meta ?? null

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SEARCH_DATA_KEY }),
      queryClient.invalidateQueries({ queryKey: FULL_SEARCH_DATA_KEY }),
    ])
  }

  return {
    organizations,
    deals,
    products,
    parentClients,
    custom_products,
    isLoading,
    isFetching,
    isReady,
    error: queryError?.message ?? null,
    meta,
    refresh,
    data,
  }
}

/**
 * Utility function to manually fetch search data (for non-React contexts)
 */
export const fetchAllSearchData = fetchFullForSearchData

export default useSearchData
